import React, { Fragment } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';

const typeStyles = {
  success: {
    Icon: CheckCircleIcon,
    iconWrap: 'bg-green-50 text-green-600',
  },
  error: {
    Icon: ExclamationCircleIcon,
    iconWrap: 'bg-red-50 text-red-600',
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    iconWrap: 'bg-amber-50 text-amber-600',
  },
  info: {
    Icon: InformationCircleIcon,
    iconWrap: 'bg-brand-soft text-brand-deep',
  },
  confirm: {
    Icon: QuestionMarkCircleIcon,
    iconWrap: 'bg-brand-soft text-brand-deep',
  },
};

const buttonClass = (intent = 'primary') => {
  const base =
    'inline-flex justify-center items-center rounded-xl px-4 py-2 text-sm font-semibold transition outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  switch (intent) {
    case 'danger':
      return `${base} bg-brick-red text-white hover:opacity-90 shadow-sm focus-visible:ring-brick-red`;
    case 'ghost':
      return `${base} bg-white text-ink-2 ring-1 ring-inset ring-line-strong hover:bg-paper-warm`;
    case 'secondary':
      return `${base} bg-ink-2 text-white hover:bg-ink shadow-sm focus-visible:ring-ink-2`;
    default:
      return `${base} bg-yellow-03 text-white hover:bg-yellow-05 shadow-sm focus-visible:ring-yellow-05`;
  }
};

const AlertDialog = ({
  open,
  onClose,
  type = 'info',
  title,
  message,
  canDismiss = true,
  primaryAction,
  secondaryAction,
}) => {
  const styles = typeStyles[type] || typeStyles.info;
  const hasActions = primaryAction || secondaryAction || type === 'confirm';

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[3000]"
        onClose={canDismiss ? onClose : () => {}}
      >
        {/* Overlay */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        {/* Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <DialogPanel className="relative w-full max-w-sm transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl ring-1 ring-black/5">
                {/* Close */}
                {canDismiss && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-05"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}

                <div className="px-5 pt-5 pb-5">
                  {/* Icon */}
                  <div
                    className={`mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full ${styles.iconWrap}`}
                  >
                    <styles.Icon className="h-6 w-6" />
                  </div>

                  {/* Title + message */}
                  <div className="text-center">
                    {!!title && (
                      <DialogTitle
                        as="h3"
                        className="text-base sm:text-lg font-semibold text-ink"
                      >
                        {title}
                      </DialogTitle>
                    )}
                    {!!message && (
                      <p className="mt-1.5 text-sm text-gray-500 whitespace-pre-line leading-relaxed">
                        {message}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {hasActions && (
                    <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
                      {secondaryAction && (
                        <button
                          type="button"
                          className={`${buttonClass(
                            secondaryAction.intent || 'ghost'
                          )} sm:flex-1`}
                          onClick={secondaryAction.onClick}
                        >
                          {secondaryAction.label || 'Cancel'}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`${buttonClass(
                          primaryAction?.intent ||
                            (type === 'confirm' ? 'danger' : 'primary')
                        )} sm:flex-1`}
                        onClick={primaryAction?.onClick || onClose}
                      >
                        {primaryAction?.label ||
                          (type === 'confirm' ? 'Confirm' : 'OK')}
                      </button>
                    </div>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AlertDialog;
